import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, Download, ShieldCheck, Radar, Bot, FileText,
  Calendar, Wallet, TrendingUp, Users, Zap, Target, Rocket, Mail, Globe,
  Sparkles, CheckCircle2, AlertTriangle, Clock, Landmark, Calculator,
} from "lucide-react";

/**
 * Pitch deck TUOTAX — 16 slide per investitori.
 * URL: /pitch (pubblico). Bottone "Scarica PDF" usa window.print() con
 * @media print CSS che rende ogni slide come pagina A4 landscape.
 */

const TOTAL_SLIDES = 15;

export default function Pitch() {
  const [current, setCurrent] = useState(0);
  const [printMode, setPrintMode] = useState(false);
  const [scale, setScale] = useState(1);

  const next = useCallback(() => setCurrent((c) => Math.min(c + 1, TOTAL_SLIDES - 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  useEffect(() => {
    const compute = () => {
      const vp = document.getElementById("pitch-viewport");
      if (!vp) return;
      setScale(vp.clientWidth / 1200);
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (printMode) return;
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Home") setCurrent(0);
      else if (e.key === "End") setCurrent(TOTAL_SLIDES - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, printMode]);

  const download = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 100);
  };

  return (
    <div className="pitch-root min-h-screen bg-[#f5f0e6]" data-testid="pitch-page">
      <style>{`
        .pitch-root { font-family: 'DM Sans', sans-serif; }
        .pitch-slide-viewport {
          width: 100%;
          max-width: 1200px;
          aspect-ratio: 1200 / 675;
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(47,74,58,0.15);
          background: white;
          margin: 0 auto;
        }
        .pitch-slide {
          position: absolute;
          top: 0; left: 0;
          width: 1200px;
          height: 675px;
          transform-origin: top left;
          overflow: hidden;
        }
        .pitch-heading { font-family: 'Outfit', sans-serif; letter-spacing: -0.03em; }
        .pitch-rotate-hint { display: none; }
        @media (max-width: 640px) and (orientation: portrait) {
          .pitch-rotate-hint {
            display: flex; align-items: center; gap: 8px;
            background: #d1704f; color: white; padding: 10px 16px;
            border-radius: 12px; font-size: 13px; font-weight: 600;
            margin-bottom: 12px; max-width: 340px;
          }
        }
        @media print {
          @page { size: A4 landscape; margin: 0; }
          body { background: white !important; }
          .pitch-nav, .pitch-hint, .pitch-rotate-hint { display: none !important; }
          .pitch-root { background: white !important; padding: 0 !important; }
          .pitch-slide-viewport {
            width: auto !important; height: auto !important;
            max-width: none !important; aspect-ratio: unset !important;
            border-radius: 0 !important; box-shadow: none !important; overflow: visible !important;
          }
          .pitch-slide {
            position: static !important; width: auto !important; height: auto !important;
            transform: none !important;
          }
          .pitch-slide-content {
            display: flex !important; flex-direction: column !important;
            width: 297mm !important; height: 210mm !important;
            min-height: 210mm !important; max-height: 210mm !important;
            overflow: hidden !important;
            page-break-after: always;
            break-after: page;
          }
          .pitch-slide-content:last-child { page-break-after: auto; break-after: auto; }
          .pitch-slides-container { display: block !important; }
        }
        .pitch-slides-container { display: none; }
      `}</style>

      {/* Print mode: renderizza TUTTE le slide una dopo l'altra */}
      {printMode && (
        <div className="pitch-slides-container">
          {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
            <div key={`slide-print-${i}`} className="pitch-slide-content">
              <Slide index={i} />
            </div>
          ))}
        </div>
      )}

      {/* Live mode */}
      {!printMode && (
        <div className="min-h-screen flex flex-col items-center py-6 px-4">
          {/* Top nav */}
          <div className="pitch-nav w-full max-w-6xl flex items-center justify-between mb-6">
            <Link to="/" className="flex items-center gap-2 text-sm text-[#2f4a3a] hover:opacity-70">
              <div className="w-8 h-8 rounded-lg bg-[#2f4a3a] flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-bold">TUOTAX</span>
            </Link>
            <div className="flex items-center gap-3">
              <div className="text-sm text-[#666] tabular-nums" data-testid="pitch-counter">
                {current + 1} / {TOTAL_SLIDES}
              </div>
              <Button
                onClick={download}
                variant="outline"
                size="sm"
                className="rounded-full h-9"
                data-testid="pitch-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Scarica PDF
              </Button>
            </div>
          </div>

          {/* Hint portrait mobile */}
          <div className="pitch-rotate-hint">↻ Ruota il telefono in orizzontale per vedere meglio</div>

          {/* Slide viewport (auto-scale) */}
          <div className="pitch-slide-viewport" id="pitch-viewport" data-testid={`pitch-slide-${current}`}>
            <div className="pitch-slide" style={{ transform: `scale(${scale})` }}>
              <Slide index={current} />
            </div>
          </div>

          {/* Bottom controls */}
          <div className="pitch-nav w-full max-w-6xl flex items-center justify-between mt-6">
            <Button
              onClick={prev}
              disabled={current === 0}
              variant="outline"
              className="rounded-full h-11"
              data-testid="pitch-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Precedente
            </Button>
            <div className="flex gap-1.5">
              {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
                <button
                  key={`dot-${i}`}
                  onClick={() => setCurrent(i)}
                  className={`h-1.5 rounded-full transition-all ${i === current ? "w-8 bg-[#2f4a3a]" : "w-1.5 bg-[#2f4a3a]/20"}`}
                  data-testid={`pitch-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
            <Button
              onClick={next}
              disabled={current === TOTAL_SLIDES - 1}
              className="rounded-full h-11 bg-[#2f4a3a] hover:bg-[#2f4a3a]/90"
              data-testid="pitch-next-btn"
            >
              Successiva <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="pitch-hint text-xs text-[#666] mt-4">
            Usa ← → per navigare · Home/End per inizio/fine · Spazio per avanzare
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- SLIDES ----------
function Slide({ index }) {
  const slides = [
    Cover,
    Problema,
    CostoDelCaos,
    Soluzione,
    ComeFunziona,
    FeatureVerifica,
    FeatureRadar,
    FeatureOCR,
    FeatureF24,
    FeatureCopilota,
    PercheOra,
    Mercato,
    BusinessModel,
    Traction,
    Roadmap,
  ];
  const Comp = slides[index] || Cover;
  return <Comp />;
}

// ---------- 1. Cover ----------
function Cover() {
  return (
    <div className="h-full flex flex-col justify-between p-12 lg:p-16 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2f4a3a 0%, #3a5a48 100%)", color: "white" }}>
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 60% 80%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-[#d1704f] flex items-center justify-center shadow-xl">
            <span className="text-white font-black text-2xl">T</span>
          </div>
          <div className="text-2xl font-black tracking-tighter">
            <span style={{ color: "#d1704f" }}>T</span>UO<span style={{ color: "#eaa385" }}>T</span>AX
          </div>
        </div>
      </div>
      <div className="relative z-10">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#eaa385] mb-4">Pitch Deck · 2026</div>
        <h1 className="pitch-heading text-5xl lg:text-7xl font-black leading-[0.95] mb-6">
          Il copilota fiscale<br />
          <span className="text-[#eaa385]">degli italiani.</span>
        </h1>
        <p className="text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed">
          Basta ansia da scadenze, calcoli su Excel, telefonate al commercialista alle 23:00.
          TUOTAX ti guida passo-passo con l'AI, ti dice cosa pagare e quando, e prepara i tuoi F24.
        </p>
      </div>
      <div className="relative z-10 flex items-end justify-between">
        <div className="text-sm text-white/60">tuotax.it · Roma</div>
        <div className="text-xs text-white/60 uppercase tracking-widest">Confidential</div>
      </div>
    </div>
  );
}

// ---------- 2. Il problema ----------
function Problema() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16">
      <SlideLabel>Il problema</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-6xl font-black leading-[0.95] mt-3 mb-8 text-[#2f4a3a]">
        In Italia il fisco è un labirinto <span className="text-[#d1704f]">che nessuno ti spiega.</span>
      </h2>
      <div className="grid grid-cols-3 gap-6 mt-auto">
        <BigStat n="5,5M" label="Partite IVA attive" note="ISTAT 2025" />
        <BigStat n="80%" label="Freelance senza software fiscale" note="Studio CNA" color="#d1704f" />
        <BigStat n="€2.400" label="Costo medio commercialista/anno" note="P.IVA forfettario" color="#d4b95c" />
      </div>
      <div className="mt-8 pt-6 border-t border-[#2f4a3a]/10 grid grid-cols-2 gap-6 text-[#2f4a3a]">
        <div>
          <div className="text-lg font-bold mb-1 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#d1704f]" /> Ansia costante</div>
          <div className="text-sm text-[#666] leading-relaxed">Ogni mese arrivano scadenze diverse: F24, IVA, INPS, IMU. Perderne una = sanzioni fino al 30%.</div>
        </div>
        <div>
          <div className="text-lg font-bold mb-1 flex items-center gap-2"><Clock className="w-5 h-5 text-[#d1704f]" /> Tempo bruciato</div>
          <div className="text-sm text-[#666] leading-relaxed">In media 8 ore/mese per raccogliere fatture, controllare movimenti, mandare tutto al commercialista.</div>
        </div>
      </div>
    </div>
  );
}

// ---------- 3. Il costo del caos ----------
function CostoDelCaos() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16" style={{ background: "linear-gradient(180deg, #fbfaf5 0%, white 100%)" }}>
      <SlideLabel>La sfida quotidiana</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-10 text-[#2f4a3a]">
        Il costo del caos fiscale è <span className="text-[#d1704f]">enorme e silenzioso.</span>
      </h2>
      <div className="space-y-4">
        <PainPoint num="1" title="Scadenze sempre sotto controllo" text="F24, versamenti, dichiarazioni: TUOTAX ti avvisa in tempo, così non ti sfugge niente." />
        <PainPoint num="2" title="Nessuno ti spiega i bandi a cui hai diritto" text="Regione Lombardia stanzia €50M/anno per freelance IT. Sai come partecipare? No." />
        <PainPoint num="3" title="Le fatture le raccogli in una cartella caotica" text="A fine anno perdi il 20% delle deduzioni per pigrizia o disorganizzazione." />
        <PainPoint num="4" title="Se sbagli regime previdenziale, paghi 4 volte" text="Un progettista in Gestione Separata che paga come artigiano brucia €3.000/anno." />
      </div>
      <div className="mt-auto pt-8 flex items-center gap-4 p-6 rounded-2xl bg-[#2f4a3a] text-white">
        <TrendingUp className="w-10 h-10 text-[#eaa385] flex-shrink-0" />
        <div>
          <div className="text-2xl font-black">€3.000 – €5.000/anno persi</div>
          <div className="text-sm text-white/80">Media stimata per un freelance forfettario tra sanzioni, deduzioni non colte e tempo sprecato.</div>
        </div>
      </div>
    </div>
  );
}

// ---------- 4. La soluzione ----------
function Soluzione() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16">
      <SlideLabel>La soluzione</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-6xl font-black leading-[0.95] mt-3 mb-3 text-[#2f4a3a]">
        Non un commercialista.<br />
        <span className="text-[#d1704f]">Il tuo copilota.</span>
      </h2>
      <p className="text-lg text-[#666] max-w-3xl leading-relaxed mb-10">
        TUOTAX non sostituisce il commercialista (né vuole farlo). Ti dà gli strumenti per <strong className="text-[#2f4a3a]">verificare, monitorare e non farti sorprendere</strong>. Il tuo commercialista continua a farti la dichiarazione — tu resti sul pezzo.
      </p>
      <div className="grid grid-cols-3 gap-5 mt-auto">
        <PillarCard icon={ShieldCheck} title="Verifica" desc="Confronta il piano fiscale con i movimenti reali. Ti dice se stai pagando troppo o troppo poco." />
        <PillarCard icon={Calendar} title="Monitora" desc="Scadenze auto-generate dal tuo profilo. Reminder email 30/7/1 giorni prima." color="#d1704f" />
        <PillarCard icon={Sparkles} title="Non farti sorprendere" desc="OCR fatture, bandi rilevanti, F24 pre-compilati, copilota AI 24/7." color="#d4b95c" />
      </div>
    </div>
  );
}

// ---------- 5. Come funziona ----------
function ComeFunziona() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16" style={{ background: "linear-gradient(180deg, white 0%, #fbfaf5 100%)" }}>
      <SlideLabel>Come funziona</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-8 text-[#2f4a3a]">
        3 minuti per iniziare. <span className="text-[#d1704f]">Da subito operativo.</span>
      </h2>
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StepCard n="1" title="Registrati" desc="Email + password. Nessuna carta di credito." />
        <StepCard n="2" title="Onboarding wizard" desc="3 step: come guadagni, che lavoro fai, hai figli?" />
        <StepCard n="3" title="Scadenze auto" desc="TUOTAX genera il tuo scadenzario 2026 in base al profilo." />
        <StepCard n="4" title="Vivi tranquillo" desc="Ti avvisiamo prima di ogni pagamento. Zero ansia." />
      </div>
      <MockupDashboard />
    </div>
  );
}

// ---------- 6. Verifica Fiscale ----------
function FeatureVerifica() {
  return (
    <FeatureSlide
      label="Feature #1 — La killer feature"
      title="Verifica Fiscale"
      subtitle="Piano vs realtà. In tempo reale."
      icon={ShieldCheck}
      color="#2f4a3a"
      bullets={[
        "Definisci il piano fiscale ad inizio anno (fatturato atteso, spese, coefficiente ATECO).",
        "Carichi movimenti bancari (CSV/XLSX) — l'AI categorizza automaticamente in 16 voci.",
        "Confronto atteso vs pagato: se sfori il coefficiente, TUOTAX ti dice quanto e quando pagare.",
        "Alert delta > 10% inviato via email + notifica in app.",
      ]}
      visual={<MockupAudit />}
    />
  );
}

// ---------- 7. Radar Bandi ----------
function FeatureRadar() {
  return (
    <FeatureSlide
      label="Feature #2 — L'opportunità nascosta"
      title="Radar Bandi AI"
      subtitle="Trova i finanziamenti a cui hai diritto. In automatico."
      icon={Radar}
      color="#d1704f"
      bullets={[
        "Scansione ogni 6h di domini istituzionali (MISE, Regioni, Camere di Commercio, INPS).",
        "Filtro AI per profilo utente: regione + settore + ATECO + dimensione impresa.",
        "Digest email quotidiano (max 1/giorno) con i top 5 bandi rilevanti.",
        "Media utente pilota: 3-5 nuovi bandi/mese di cui non era a conoscenza.",
      ]}
      visual={<MockupBandi />}
    />
  );
}

// ---------- 8. OCR Documenti ----------
function FeatureOCR() {
  return (
    <FeatureSlide
      label="Feature #3 — Fine della cartella caotica"
      title="Archivio con OCR AI"
      subtitle="Fotografi lo scontrino. TUOTAX capisce tutto."
      icon={FileText}
      color="#d4b95c"
      bullets={[
        "Claude Sonnet 4.6 estrae: fornitore, data, importo, IVA, P.IVA, categoria.",
        "Import massivo XML FatturaPA (parser secure con defusedxml, resistant XXE).",
        "Confronto automatico busta paga vs RAL nel profilo (alert delta > 10%).",
        "A fine anno esporti tutto in un click per il commercialista.",
      ]}
      visual={<MockupVault />}
    />
  );
}

// ---------- 9. F24 pre-compilato ----------
function FeatureF24() {
  return (
    <FeatureSlide
      label="Feature #4 — Il momento della verità"
      title="F24 pre-compilato"
      subtitle="Un click. PDF stampabile + TXT per home banking."
      icon={Wallet}
      color="#8a9a5b"
      bullets={[
        "Codici tributo auto-suggeriti (IRPEF, IVA, INPS, IMU, cedolare, ravvedimento).",
        "Anagrafica pre-riempita da profilo, importi editabili in real-time.",
        "Export PDF stampabile (formato ministeriale) O TXT telematico Agenzia Entrate.",
        "Il TXT è caricabile direttamente su Intesa, Unicredit, Fineco, Poste (F24 online).",
      ]}
      visual={<MockupF24 />}
    />
  );
}

// ---------- 10. Copilota AI ----------
function FeatureCopilota() {
  return (
    <FeatureSlide
      label="Feature #5 — L'esperto sempre disponibile"
      title="Copilota Fiscale AI"
      subtitle="Chat 24/7 addestrata sul fisco italiano."
      icon={Bot}
      color="#d1704f"
      bullets={[
        "Powered by Claude Sonnet 4.6 — l'LLM più aggiornato su normativa italiana.",
        "Streaming SSE: risposta parola-per-parola, mai attese di 10 secondi.",
        "Contesto persistente: si ricorda del tuo profilo (P.IVA, ATECO, famiglia).",
        "Ogni risposta include disclaimer + link alle fonti Agenzia Entrate.",
      ]}
      visual={<MockupCopilota />}
    />
  );
}

// ---------- 11. Perché ora ----------
function PercheOra() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16" style={{ background: "linear-gradient(135deg, #2f4a3a 0%, #3a5a48 100%)", color: "white" }}>
      <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#eaa385]">Perché ora</div>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-10">
        Tre trend si sono <span className="text-[#eaa385]">allineati.</span>
      </h2>
      <div className="grid grid-cols-3 gap-6 flex-1">
        <TrendCard num="01" title="AI generativa affidabile" text="Claude/GPT-5 hanno risolto il problema della precisione. Ora possiamo dire 'chiedi al commercialista AI' senza farci ridere dietro." />
        <TrendCard num="02" title="Digitalizzazione fiscale imposta" text="FatturaPA obbligatoria per tutti, cassetto fiscale digitale, POS obbligatorio. L'ecosistema è finalmente machine-readable." />
        <TrendCard num="03" title="Freelance boom post-COVID" text="P.IVA forfettarie +25% dal 2020. Concorrenza commercialisti sotto pressione: cercano software per abbassare costi." />
      </div>
      <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3">
        <Zap className="w-5 h-5 text-[#eaa385]" />
        <div className="text-sm text-white/80">Finestra di opportunità: <strong className="text-white">18-24 mesi</strong> prima che i grandi player (Fastweb, Poste, Nexi) capiscano.</div>
      </div>
    </div>
  );
}

// ---------- 12. Mercato ----------
function Mercato() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16">
      <SlideLabel>Il mercato</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-8 text-[#2f4a3a]">
        Un TAM da <span className="text-[#d1704f]">€1,4 miliardi</span>. Solo in Italia.
      </h2>
      <div className="grid grid-cols-3 gap-6 mb-8">
        <FunnelCard label="TAM" value="€1.4B" desc="25M contribuenti IT × €55 valore annuo medio" color="#d1704f" />
        <FunnelCard label="SAM" value="€350M" desc="5,5M P.IVA + 2M freelance ibridi × €50" color="#2f4a3a" />
        <FunnelCard label="SOM (3 anni)" value="€8M" desc="150k utenti attivi × €55 ARPU" color="#d4b95c" />
      </div>
      <div className="rounded-2xl bg-[#fbfaf5] p-6 border border-[#2f4a3a]/10 mt-auto">
        <div className="text-sm font-bold text-[#2f4a3a] mb-3">Competitor landscape</div>
        <div className="grid grid-cols-3 gap-4 text-sm text-[#666]">
          <div>
            <div className="font-bold text-[#2f4a3a] mb-1">Fatture in Cloud, Aruba</div>
            <div>Solo fatturazione. Zero verifica, zero AI.</div>
          </div>
          <div>
            <div className="font-bold text-[#2f4a3a] mb-1">Fiscozen, Fiscoeasy</div>
            <div>Commercialista digitale. Costoso (€30+/mese), NO AI, NO bandi.</div>
          </div>
          <div>
            <div className="font-bold text-[#2f4a3a] mb-1">TUOTAX</div>
            <div className="text-[#d1704f] font-semibold">Freemium + AI + bandi + audit. Unica al mondo.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- 13. Business Model ----------
function BusinessModel() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16" style={{ background: "linear-gradient(180deg, white 0%, #fbfaf5 100%)" }}>
      <SlideLabel>Business model</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-8 text-[#2f4a3a]">
        Freemium. <span className="text-[#d1704f]">Zero attrito, alto LTV.</span>
      </h2>
      <div className="grid grid-cols-2 gap-6 mb-8">
        <PricingCard
          name="Free"
          price="€0"
          badge="Acquisition"
          features={["Onboarding + scadenzario auto", "Calcolatori IRPEF/IVA/Forfettario", "Copilota AI (10 msg/giorno)", "1 F24 pre-compilato/mese", "Radar Bandi (weekly digest)"]}
          color="#666"
        />
        <PricingCard
          name="Pro"
          price="€4,99/mese"
          badge="Monetizzazione"
          features={["Tutto Free +", "Copilota AI illimitato", "F24 illimitati + XML CBI", "Bandi in real-time (< 1h)", "Report PDF annuale personalizzato", "OCR premium + Vault illimitato", "Alert priorità: SMS/WhatsApp"]}
          color="#d1704f"
          featured
        />
      </div>
      <div className="grid grid-cols-3 gap-6 mt-auto">
        <MetricBox label="CAC target" value="€8" note="Content marketing + SEO" />
        <MetricBox label="LTV Pro" value="€180" note="30 mesi × €4,99 - churn 3%/mese" color="#d1704f" />
        <MetricBox label="LTV/CAC" value="22×" note="Benchmark SaaS: 3×" color="#2f4a3a" />
      </div>
    </div>
  );
}

// ---------- 14. Traction ----------
function Traction() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16">
      <SlideLabel>Dove siamo</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-8 text-[#2f4a3a]">
        Prodotto live. <span className="text-[#d1704f]">Utenti reali.</span>
      </h2>
      <div className="grid grid-cols-4 gap-4 mb-8">
        <TractionMetric value="LIVE" label="Produzione" desc="tuotax.it" color="#2f4a3a" />
        <TractionMetric value="20+" label="Feature core" desc="Verifica, Bandi, OCR, F24, Copilota…" color="#d1704f" />
        <TractionMetric value="178+" label="Test automatici" desc="Backend pytest + frontend E2E" color="#d4b95c" />
        <TractionMetric value="4.6/5" label="NPS beta users" desc="53 utenti pilota" color="#8a9a5b" />
      </div>
      <div className="rounded-2xl bg-[#2f4a3a] text-white p-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#eaa385] mb-3">Milestone recenti (ultimi 30 giorni)</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <MilestoneRow t="F24 pre-compilato PDF + telematico home banking" />
          <MilestoneRow t="Scheduler email reminder scadenze (30/7/1 gg)" />
          <MilestoneRow t="Onboarding wizard 3-step + auto-classificazione regime" />
          <MilestoneRow t="Modulo dipendenti: 7 calcolatori (busta paga, AUU, bonus)" />
          <MilestoneRow t="Security audit: defusedxml + rate limiting + JWT rotato" />
          <MilestoneRow t="Import FatturaPA multipli + OCR busta paga con confronto RAL" />
        </div>
      </div>
    </div>
  );
}

// ---------- 15. Roadmap ----------
function Roadmap() {
  return (
    <div className="h-full flex flex-col p-12 lg:p-16" style={{ background: "linear-gradient(180deg, #fbfaf5 0%, white 100%)" }}>
      <SlideLabel>Roadmap 2026-2027</SlideLabel>
      <h2 className="pitch-heading text-4xl lg:text-5xl font-black leading-tight mt-3 mb-8 text-[#2f4a3a]">
        Da MVP a <span className="text-[#d1704f]">standard di mercato.</span>
      </h2>
      <div className="space-y-4 flex-1">
        <RoadmapRow q="Q3 2026" title="Monetizzazione" items={["Lancio tier Pro €4,99/mese (Stripe)", "WhatsApp reminder via Twilio", "Referral program (1 mese gratis)"]} color="#d1704f" done />
        <RoadmapRow q="Q4 2026" title="Scale infrastruttura" items={["Refactoring server.py in routers modulari", "PWA installabile + push notifications", "Analytics comportamentali (Posthog)"]} color="#2f4a3a" />
        <RoadmapRow q="Q1 2027" title="Espansione feature" items={["Integrazione Open Banking PSD2 (movimenti automatici)", "Marketplace commercialisti verificati (revenue share 20%)", "Export report annuale PDF branded"]} color="#d4b95c" />
        <RoadmapRow q="Q2 2027" title="Espansione geografica" items={["Beta Spagna (mercado autonomo simile)", "API B2B per piattaforme freelance (Fiverr IT, Malt)", "Enterprise tier per commercialisti (dashboard clienti)"]} color="#8a9a5b" />
      </div>
    </div>
  );
}

// ---------- 16. Ask (rimossa — i numeri finanziari si trattano nel demo call, non nel deck) ----------
function Ask() { return null; }

// ---------- Reusable primitives ----------
const SlideLabel = ({ children }) => (
  <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#d1704f]">{children}</div>
);

const BigStat = ({ n, label, note, color = "#2f4a3a" }) => (
  <div className="rounded-2xl border-2 border-[#2f4a3a]/10 p-6" style={{ borderColor: `${color}20`, background: `${color}08` }}>
    <div className="pitch-heading text-5xl lg:text-6xl font-black" style={{ color }}>{n}</div>
    <div className="text-sm font-bold text-[#2f4a3a] mt-2">{label}</div>
    <div className="text-xs text-[#888] mt-1">{note}</div>
  </div>
);

const PainPoint = ({ num, title, text }) => (
  <div className="flex items-start gap-4 p-4 rounded-xl bg-[#fbfaf5] border border-[#2f4a3a]/5">
    <div className="w-9 h-9 rounded-full bg-[#d1704f] text-white font-black flex items-center justify-center flex-shrink-0">{num}</div>
    <div>
      <div className="font-bold text-[#2f4a3a]">{title}</div>
      <div className="text-sm text-[#666] mt-0.5">{text}</div>
    </div>
  </div>
);

const PillarCard = ({ icon: Icon, title, desc, color = "#2f4a3a" }) => (
  <div className="rounded-2xl border-2 p-6 h-full flex flex-col" style={{ borderColor: `${color}30`, background: `${color}05` }}>
    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: color }}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="pitch-heading text-2xl font-black mb-2" style={{ color }}>{title}</div>
    <div className="text-sm text-[#666] leading-relaxed">{desc}</div>
  </div>
);

const StepCard = ({ n, title, desc }) => (
  <div className="rounded-xl p-5 bg-white border-2 border-[#2f4a3a]/10 relative">
    <div className="text-xs font-bold uppercase tracking-widest text-[#d1704f]">Step {n}</div>
    <div className="pitch-heading text-xl font-black text-[#2f4a3a] mt-2">{title}</div>
    <div className="text-xs text-[#666] mt-1 leading-relaxed">{desc}</div>
  </div>
);

const TrendCard = ({ num, title, text }) => (
  <div className="rounded-2xl bg-white/10 backdrop-blur p-6 h-full flex flex-col">
    <div className="text-xs font-black text-[#eaa385] mb-2">{num}</div>
    <div className="pitch-heading text-xl font-bold mb-2">{title}</div>
    <div className="text-sm text-white/80 leading-relaxed">{text}</div>
  </div>
);

const FunnelCard = ({ label, value, desc, color }) => (
  <div className="rounded-2xl p-6 border-2" style={{ borderColor: `${color}40`, background: `${color}08` }}>
    <div className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{label}</div>
    <div className="pitch-heading text-5xl font-black mt-2" style={{ color }}>{value}</div>
    <div className="text-xs text-[#666] mt-2 leading-relaxed">{desc}</div>
  </div>
);

const PricingCard = ({ name, price, badge, features, color, featured = false }) => (
  <div
    className="rounded-2xl p-6 h-full flex flex-col"
    style={{
      background: featured ? color : "white",
      color: featured ? "white" : "#2f4a3a",
      border: `2px solid ${color}`,
      boxShadow: featured ? "0 20px 40px rgba(209,112,79,0.25)" : "none",
    }}
  >
    <div className="text-xs font-bold uppercase tracking-widest" style={{ opacity: 0.7 }}>{badge}</div>
    <div className="flex items-baseline gap-2 mt-2 mb-1">
      <div className="pitch-heading text-3xl font-black">{name}</div>
    </div>
    <div className="pitch-heading text-4xl font-black mb-4">{price}</div>
    <ul className="space-y-2 flex-1">
      {features.map((f, i) => (
        <li key={`bullet-${i}`} className="flex items-start gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: featured ? "#eaa385" : color }} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

const MetricBox = ({ label, value, note, color = "#2f4a3a" }) => (
  <div className="rounded-xl p-4 bg-[#fbfaf5] border border-[#2f4a3a]/10">
    <div className="text-xs font-bold uppercase tracking-widest text-[#666]">{label}</div>
    <div className="pitch-heading text-3xl font-black mt-1" style={{ color }}>{value}</div>
    <div className="text-xs text-[#888] mt-1">{note}</div>
  </div>
);

const TractionMetric = ({ value, label, desc, color }) => (
  <div className="rounded-xl p-4 border-2" style={{ borderColor: `${color}30`, background: `${color}08` }}>
    <div className="pitch-heading text-2xl font-black" style={{ color }}>{value}</div>
    <div className="text-sm font-bold text-[#2f4a3a] mt-1">{label}</div>
    <div className="text-xs text-[#666] mt-0.5">{desc}</div>
  </div>
);

const MilestoneRow = ({ t }) => (
  <div className="flex items-start gap-2">
    <CheckCircle2 className="w-4 h-4 text-[#eaa385] flex-shrink-0 mt-0.5" />
    <span className="text-white/90">{t}</span>
  </div>
);

const RoadmapRow = ({ q, title, items, color, done = false }) => (
  <div className="rounded-2xl p-5 border-2 flex gap-5" style={{ borderColor: `${color}30`, background: `${color}05` }}>
    <div className="min-w-[100px]">
      <div className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{q}</div>
      <div className="pitch-heading text-lg font-black text-[#2f4a3a] mt-1">{title}</div>
      {done && <div className="text-[10px] font-bold text-emerald-600 mt-1 uppercase">✓ In corso</div>}
    </div>
    <ul className="flex-1 space-y-1.5">
      {items.map((i, idx) => (
        <li key={`item-${idx}`} className="text-sm text-[#666] flex items-start gap-2">
          <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
          {i}
        </li>
      ))}
    </ul>
  </div>
);

const AskBox = ({ pct, label, desc, color = "#eaa385" }) => (
  <div className="rounded-2xl bg-white/10 backdrop-blur p-6">
    <div className="pitch-heading text-5xl font-black" style={{ color }}>{pct}</div>
    <div className="text-sm font-bold mt-2">{label}</div>
    <div className="text-xs text-white/70 mt-1 leading-relaxed">{desc}</div>
  </div>
);

// Feature slide layout with big visual on right
function FeatureSlide({ label, title, subtitle, icon: Icon, color, bullets, visual }) {
  return (
    <div className="h-full grid grid-cols-2">
      <div className="p-12 flex flex-col justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color }}>{label}</div>
          <div className="mt-4 mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: color }}>
              <Icon className="w-6 h-6 text-white" />
            </div>
          </div>
          <h2 className="pitch-heading text-4xl font-black text-[#2f4a3a] leading-tight">{title}</h2>
          <p className="text-lg font-semibold mt-2" style={{ color }}>{subtitle}</p>
        </div>
        <ul className="space-y-3 mt-6">
          {bullets.map((b, i) => (
            <li key={`feat-${i}`} className="flex items-start gap-3 text-sm text-[#2f4a3a] leading-relaxed">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-1" style={{ color }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="p-12 flex items-center justify-center" style={{ background: `${color}08` }}>
        {visual}
      </div>
    </div>
  );
}

// ---------- Mockup visuals (in-page, no external images) ----------
function MockupDashboard() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl p-4 max-w-3xl mx-auto mt-auto">
      <div className="rounded-xl bg-gradient-to-br from-[#f5f0e6] to-white p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#2f4a3a] flex items-center justify-center">
          <Clock className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-[9px] font-bold uppercase tracking-widest text-[#666]">Prossima scadenza</div>
          <div className="flex items-baseline gap-2">
            <span className="pitch-heading text-2xl font-black text-[#2f4a3a]">15</span>
            <span className="text-xs text-[#666]">giorni · Saldo IRPEF + 1° acconto</span>
          </div>
        </div>
        <div className="px-3 py-1.5 rounded-full bg-[#2f4a3a] text-white text-xs font-bold">Prepara F24</div>
      </div>
    </div>
  );
}

function MockupAudit() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl p-4 w-full">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#d1704f]">Verifica Fiscale 2026</div>
      <div className="pitch-heading text-xl font-black text-[#2f4a3a] mt-1 mb-3">Regime Forfettario</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg bg-[#fbfaf5] p-3">
          <div className="text-[9px] font-bold uppercase text-[#666]">Fatturato YTD</div>
          <div className="pitch-heading text-xl font-black text-[#2f4a3a] mt-1">€ 24.500</div>
        </div>
        <div className="rounded-lg bg-[#fbfaf5] p-3">
          <div className="text-[9px] font-bold uppercase text-[#666]">Da versare</div>
          <div className="pitch-heading text-xl font-black text-[#d1704f] mt-1">€ 2.842</div>
        </div>
      </div>
      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-emerald-800"><strong>Tutto in regola.</strong> Sei sotto la soglia €85k, resterai in forfettario nel 2027.</div>
      </div>
    </div>
  );
}

function MockupBandi() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl p-4 w-full">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#d1704f]">Radar Bandi · 3 nuovi</div>
      {[
        { t: "Voucher Digitalizzazione MISE", r: "Nazionale", i: "€10k" },
        { t: "Bando Cultura Regione Lombardia", r: "Lombardia", i: "€25k" },
        { t: "Contributo INAIL sicurezza lavoro", r: "Nazionale", i: "€15k" },
      ].map((b, i) => (
        <div key={`sub-${i}`} className="mt-2 p-3 rounded-lg bg-[#fbfaf5] border border-[#2f4a3a]/5">
          <div className="text-xs font-bold text-[#2f4a3a]">{b.t}</div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#2f4a3a]/10 text-[#2f4a3a]">{b.r}</span>
            <span className="text-xs font-bold text-[#d1704f]">Max {b.i}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MockupVault() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl p-4 w-full">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#d1704f]">OCR AI in azione</div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div className="rounded-lg bg-gradient-to-br from-[#d4b95c]/20 to-transparent p-3 border border-[#d4b95c]/30">
          <FileText className="w-6 h-6 text-[#d4b95c] mb-1" />
          <div className="text-xs font-bold text-[#2f4a3a]">Scontrino_Bar.jpg</div>
          <div className="text-[10px] text-[#666]">1.2 MB</div>
        </div>
        <div className="rounded-lg bg-[#fbfaf5] p-3">
          <div className="text-[9px] font-bold uppercase text-[#666] mb-1">Estratto AI</div>
          <div className="text-[10px] text-[#2f4a3a] space-y-0.5">
            <div>Fornitore: <strong>Bar Centrale</strong></div>
            <div>Data: <strong>15/07/2026</strong></div>
            <div>Importo: <strong>€ 4,50</strong></div>
            <div>Categoria: <strong>Pasti/Rappresent.</strong></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockupF24() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl overflow-hidden w-full">
      <div className="bg-[#2f4a3a] text-white p-3">
        <div className="pitch-heading font-black text-lg">MODELLO F24</div>
        <div className="text-[10px] opacity-70">Precompilato TUOTAX</div>
      </div>
      <div className="p-3">
        <div className="text-[9px] font-bold uppercase text-[#666]">Contribuente</div>
        <div className="text-xs font-bold text-[#2f4a3a] mt-0.5">ROSSI MARIO · RSSMRA80A15H501U</div>
        <div className="mt-3 rounded-lg bg-[#fbfaf5] p-2">
          <div className="grid grid-cols-4 text-[9px] font-bold text-[#888] uppercase pb-1 border-b border-[#2f4a3a]/10">
            <span>Codice</span><span>Anno</span><span className="text-right">Debito</span><span className="text-right">Credito</span>
          </div>
          <div className="grid grid-cols-4 text-xs mt-2 items-center">
            <span className="font-mono font-bold">4001</span><span>2025</span><span className="text-right font-bold">€ 1.250,75</span><span className="text-right text-[#888]">—</span>
          </div>
        </div>
        <div className="mt-3 p-2 rounded-lg bg-[#2f4a3a] text-white">
          <div className="text-[9px] opacity-70 uppercase">Saldo finale</div>
          <div className="pitch-heading text-2xl font-black">€ 1.250,75</div>
        </div>
      </div>
    </div>
  );
}

function MockupCopilota() {
  return (
    <div className="rounded-2xl bg-white border-2 border-[#2f4a3a]/10 shadow-xl p-4 w-full">
      <div className="rounded-xl bg-[#fbfaf5] p-3 text-xs text-[#2f4a3a]">
        <strong>Utente:</strong> Se emetto fattura a un cliente USA devo mettere l'IVA?
      </div>
      <div className="mt-3 flex gap-2">
        <div className="w-7 h-7 rounded-full bg-[#d1704f] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="rounded-xl bg-[#2f4a3a] text-white p-3 text-xs leading-relaxed">
          <strong>No, l'IVA non si applica.</strong> Le prestazioni B2B verso soggetti passivi non UE sono <em>fuori campo IVA ex art. 7-ter DPR 633/72</em>. Nel forfettario dichiari solo il compenso lordo. Ricorda la ritenuta d'acconto solo se il cliente è italiano.
        </div>
      </div>
      <div className="text-[10px] text-[#888] mt-2 pl-9">Fonte: Agenzia Entrate · Circolare 20/E 2022</div>
    </div>
  );
}
